import React, { ReactElement } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { TabConfig } from '@odin-labs/components';
import {
  GetJobsiteContainerMembersDocument,
  UserAssignmentType,
  UserRoleKey,
  useGetContractorsQuery,
  useGetJobsiteContainerQuery,
  useGetRolesQuery,
} from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import { Container } from 'components/container';
import { RoutedTabsPages, useRoutedTabs } from 'components/tabs';
import { AuthContext } from 'auth';
import { to } from 'acl';
import { ensureNonEmptyItems, useBoolean } from 'utils';
import { AuthUser } from 'auth/types';
import { AddUserModal } from 'containers/users/modals';
import { useFlags } from 'featureFlags';
import { JobsiteOverviewTabContainer } from 'containers/jobsite/tabs/jobsiteOverview/JobsiteOverviewTabContainer';
import { JobsiteTabApi, JobsiteTabProps } from './types';
import { JobsiteHeader } from './components';
import {
  JobsiteEquipmentTabContainer,
  JobsiteEquipmentHistoryTabContainer,
  JobsiteConnectionsTabContainer,
  JobsiteInfoTabContainer,
  JobsiteUsersTabContainer,
  JobsiteWorkersTabContainer,
  JobsiteContractorsTabContainer,
} from './tabs';
import { AddContractorAssignmentModal } from './modals';
import { JobsitePulseTabContainer } from './tabs/jobsitePulse';
import { JobsiteSchedulesTabContainer } from './tabs/jobsiteSchedules';
import { JobsiteTempBadgesTabContainer } from './tabs/jobsiteTempBadges';
import { TempBadgeModal } from './tabs/jobsiteTempBadges/modals';

const refetchQueries = [GetJobsiteContainerMembersDocument];

const getTabsConfig = ({
  user,
  jobsiteId,
  isPulseEnabled,
  isSchedulingEnabled,
  showNewJobsiteDashboard,
  connectionsEnabled,
}: {
  user: AuthUser;
  jobsiteId: string;
  isPulseEnabled: boolean;
  isSchedulingEnabled: boolean;
  showNewJobsiteDashboard: boolean;
  connectionsEnabled: boolean;
}): TabConfig<JobsiteTabProps>[] => {
  // Advanced tabs are not visible to contractors
  const canSeeAdvancedTabs = !user.isContractor;
  return ensureNonEmptyItems<TabConfig<JobsiteTabProps>>([
    showNewJobsiteDashboard && {
      name: 'overview',
      text: 'Overview',
      component: JobsiteOverviewTabContainer,
    },
    !showNewJobsiteDashboard && {
      name: 'info',
      text: 'Info',
      component: JobsiteInfoTabContainer,
    },
    isPulseEnabled && {
      name: 'pulse',
      text: 'Pulse',
      relativePath: '/pulse',
      component: JobsitePulseTabContainer,
    },
    isSchedulingEnabled &&
      user.isAllowed(to.seeSchedules, jobsiteId) && {
        name: 'schedules',
        text: 'Schedules',
        relativePath: '/schedules',
        component: JobsiteSchedulesTabContainer,
      },
    {
      name: 'contractors',
      text: 'Contractors',
      relativePath: '/contractors',
      component: JobsiteContractorsTabContainer,
    },
    {
      name: 'workers',
      text: 'Workers',
      relativePath: '/workers',
      component: JobsiteWorkersTabContainer,
    },
    user.isAllowed(to.seeTempBadges, jobsiteId) && {
      name: 'tempBadges',
      text: 'Temporary Badges',
      relativePath: '/temp-badges',
      component: JobsiteTempBadgesTabContainer,
    },
    canSeeAdvancedTabs &&
      user.isAllowed(to.seeJobsiteUsers, jobsiteId) && {
        name: 'users',
        text: 'Users',
        relativePath: '/users',
        component: JobsiteUsersTabContainer,
      },
    connectionsEnabled &&
      user.isAllowed(to.seeJobsiteConnections, jobsiteId) && {
        name: 'connections',
        text: 'Connections',
        relativePath: '/connections',
        component: JobsiteConnectionsTabContainer,
      },
    canSeeAdvancedTabs && {
      name: 'equipment',
      text: 'Equipment',
      relativePath: '/equipment',
      component: JobsiteEquipmentTabContainer,
    },
    canSeeAdvancedTabs && {
      name: 'equipmentHistory',
      text: 'Equipment History',
      relativePath: '/equipment-history',
      component: JobsiteEquipmentHistoryTabContainer,
    },
  ]);
};

export function JobsiteContainer(): ReactElement {
  const {
    jobsitePulseEnabled: isPulseEnabled,
    procoreSelfServiceEnabled,
    showNewJobsiteDashboard,
    connectionsEnabled,
  } = useFlags();
  const history = useHistory();
  const location = useLocation();

  const { jobsiteId } = useParams<{ jobsiteId: string }>();
  const baseRoute = `/jobsite/${jobsiteId}`;
  const usersRoute = `${baseRoute}/users`;
  const contractorsRoute = `${baseRoute}/contractors`;
  const tempBadgesRoute = `${baseRoute}/temp-badges`;

  const {
    value: isAddJobsiteUserModalOpen,
    setTrue: openAddJobsiteUserModal,
    setFalse: closeAddJobsiteUserModal,
  } = useBoolean(false);

  const {
    value: isAddContractorAssignmentModalOpen,
    setTrue: openAddContractorAssignmentModal,
    setFalse: closeAddContractorAssignmentModal,
  } = useBoolean(false);

  const { currentUser: user } = React.useContext(AuthContext);

  const {
    data: jobsiteData,
    loading: jobsiteLoading,
    error: jobsiteError,
  } = useGetJobsiteContainerQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: { jobsiteId },
  });

  const {
    data: contractorsData,
    loading: contractorsLoading,
    error: contractorsError,
  } = useGetContractorsQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const jobsite = jobsiteData?.getJobsite;
  const contractorsCount = jobsite?.jobsiteContractors.count.toString() ?? '';
  const usersCount = jobsite?.jobsiteMembers.count.toString() ?? '';
  const workersCount = jobsite?.jobsiteWorkers.count.toString() ?? '';
  const tempBadgesCount = jobsite?.tempBadges.count.toString() ?? '';
  const equipmentCount = '';
  const { schedulingEnabled: isSchedulingEnabled } = jobsiteLoading
    ? // features are enabled while loading the jobsite data
      // so that the tabs are still available when the data loads
      { schedulingEnabled: true }
    : jobsite.featuresModule ?? {};

  const { data: rolesData } = useGetRolesQuery({ fetchPolicy: 'network-only' });
  const userRoles = rolesData?.getRoles.filter(
    ({ key }) => key !== UserRoleKey.Worker,
  );

  const { isProcoreEnabled, procoreProjectVendors } = React.useMemo(() => {
    return {
      isProcoreEnabled: !!(
        jobsite?.procoreCompanyId &&
        jobsite?.procoreProjectId &&
        procoreSelfServiceEnabled
      ),
      procoreProjectVendors: jobsite?.procoreProjectVendors?.edges.map(
        (pv) => pv.node,
      ),
    };
  }, [
    jobsite?.procoreCompanyId,
    jobsite?.procoreProjectId,
    jobsite?.procoreProjectVendors,
    procoreSelfServiceEnabled,
  ]);

  const availableContractorsToAssign = React.useMemo(() => {
    const contractors =
      contractorsData?.getCurrentSession.user.contractors.edges.map(
        ({ node }) => node,
      );
    const assignedContractorIds = jobsite?.jobsiteContractors.edges.map(
      ({ node }) => node.contractor.contractorId,
    );
    return contractors?.filter(
      (c) => !assignedContractorIds?.includes(c.contractorId),
    );
  }, [jobsite, contractorsData]);

  const [tabApi, setTabApi] = React.useState<JobsiteTabApi>(null);

  const tabsCount: Record<string, string> = {
    contractors: tabApi?.contractorsCountText ?? contractorsCount,
    workers: workersCount,
    tempBadges: tabApi?.tempBadgesCountText ?? tempBadgesCount,
    users: tabApi?.usersCountText ?? usersCount,
    equipment: equipmentCount,
  };

  const tabsConfigWithBadges = getTabsConfig({
    user,
    jobsiteId,
    isPulseEnabled,
    isSchedulingEnabled,
    showNewJobsiteDashboard,
    connectionsEnabled,
  }).map((tab) => ({
    ...tab,
    badge: tabsCount[tab.name],
  }));
  const { tabs, currentTab } = useRoutedTabs({
    tabsConfig: tabsConfigWithBadges,
    baseRoute,
  });

  const closeModalAndNavigateToJobsiteUsersIfNeeded =
    React.useCallback((): void => {
      if (location.pathname !== usersRoute) {
        history.push(usersRoute);
      }
      closeAddJobsiteUserModal();
    }, [closeAddJobsiteUserModal, history, location]);

  const closeModalAndNavigateToContractorsIfNeeded =
    React.useCallback((): void => {
      if (location.pathname !== contractorsRoute) {
        history.push(contractorsRoute);
      }
      closeAddJobsiteUserModal();
    }, [closeAddJobsiteUserModal, history, location]);

  const navigateToTempBadgesOrRefresh = React.useCallback((): void => {
    if (location.pathname !== tempBadgesRoute) {
      history.push(tempBadgesRoute);
    } else {
      tabApi.refetchData?.();
    }
  }, [history, location, tabApi]);

  const onTabApiChange = React.useCallback(
    (currentTabApi: Partial<JobsiteTabApi>) =>
      setTabApi((prev) => ({ ...prev, ...currentTabApi })),
    [],
  );

  const tabsPageProps: JobsiteTabProps = {
    jobsiteId,
    timeZone: jobsite?.timeZone,
    loading: jobsiteLoading || contractorsLoading,
    error: jobsiteError,
    isProcoreEnabled,
    procoreProjectVendors,
    onTabApiChange,
    baseRoute,
  };

  const error = jobsiteError || contractorsError;
  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <Container className="jobsite-container">
      <JobsiteHeader
        jobsite={jobsite}
        baseRoute={baseRoute}
        tabsProps={{ tabs, currentTab }}
        onReloadPressed={tabApi?.refetchData}
        openAddContractorAssignmentModal={openAddContractorAssignmentModal}
        openAddJobsiteUserModal={openAddJobsiteUserModal}
      />
      <RoutedTabsPages
        baseRoute={baseRoute}
        tabs={tabs}
        componentProps={tabsPageProps}
      />
      {jobsite && (
        <>
          <AddUserModal
            isOpen={isAddJobsiteUserModalOpen}
            closeModal={closeAddJobsiteUserModal}
            onConfirm={closeModalAndNavigateToJobsiteUsersIfNeeded}
            jobsite={jobsite}
            refetchQueries={refetchQueries}
            userRoleType={UserAssignmentType.Jobsite}
            userRoles={userRoles}
          />
          <AddContractorAssignmentModal
            isOpen={isAddContractorAssignmentModalOpen}
            closeModal={closeAddContractorAssignmentModal}
            onConfirm={closeModalAndNavigateToContractorsIfNeeded}
            jobsite={jobsite}
            contractors={availableContractorsToAssign}
            isProcoreEnabled={isProcoreEnabled}
            procoreProjectVendors={procoreProjectVendors}
          />
          <TempBadgeModal
            navigateToTempBadgesOrRefresh={navigateToTempBadgesOrRefresh}
            jobsite={jobsite}
          />
        </>
      )}
    </Container>
  );
}
